/**
 * 根拠資料（2026-10-07）— 分析成績書・規格書・レシピの PDF / 画像を、この公式値に紐付けて保管する。
 * ⚠️ 保管するだけで、AI には送らない（読むのは御社と、求めがあったときの運営だけ）。1 ファイル 10MB・1 件あたり 20 ファイルまで。
 */
import { useEffect, useState } from "react";
import { api, documentUrl, type EvidenceDocument } from "../api";

const KINDS: Record<string, string> = { lab_report: "分析成績書", spec_sheet: "規格書", recipe: "レシピ", other: "その他" };

export function DocumentsCard({ entryId }: { entryId: string }) {
  const [docs, setDocs] = useState<EvidenceDocument[] | null>(null);
  const [kind, setKind] = useState("lab_report");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => void api.documents(entryId).then((r) => (r.ok ? setDocs(r.data) : setError(r.error)));
  useEffect(load, [entryId]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const r = await api.uploadDocument(entryId, kind, file);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    load();
  };

  return (
    <div className="card">
      <h2>根拠資料</h2>
      <p className="muted small">
        分析成績書・規格書・レシピなど、この値の根拠になる資料を保管できます（PDF・画像・10MB まで）。保管するだけで、AI には送りません。
      </p>
      {docs && docs.length > 0 && (
        <ul className="list">
          {docs.map((d) => (
            <li key={d.id} className="list-row">
              <span>
                <a href={documentUrl(d.id)}>{d.filename ?? "資料"}</a>{" "}
                <span className="muted small">
                  {KINDS[d.kind] ?? d.kind}
                  {d.sizeBytes != null && ` ・ ${Math.max(1, Math.round(d.sizeBytes / 1024)).toLocaleString("ja-JP")} KB`}
                </span>
              </span>
              <button
                type="button"
                className="btn-link small"
                onClick={() => void api.deleteDocument(d.id).then((r) => (r.ok ? load() : setError(r.error)))}
              >
                削除
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="toolbar">
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="資料の種類">
          {Object.entries(KINDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => void upload(e.target.files?.[0])} aria-label="資料を選ぶ" />
        {busy && <span className="muted small">保管しています…</span>}
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
